/**
 * <social-proof>
 * Conveyor-style avatar stack: everyone slides one step left,
 * leftmost fades out, a new one fades in on the right.
 * Layout width is fixed — text never shifts.
 */
if (!customElements.get('social-proof')) {
  class SocialProof extends HTMLElement {
    constructor() {
      super();
      this.avatarsEl = null;
      this.timer = null;
      this.isHovering = false;
      this.isInView = true;
      this.reducedMotion = false;
      this.canHover = false;
      this.isAnimating = false;
      this.transitionMs = 550;
      this.tones = 5;
      this.pool = [
        'JL', 'SP', 'AN', 'MR', 'LC', 'DG', 'FV', 'CM', 'PB', 'TR',
        'AG', 'NS', 'RH', 'IK', 'EW', 'BQ', 'UY', 'HT', 'KV', 'MA',
        'SO', 'LI', 'PE', 'RO', 'CA', 'DI', 'FE', 'GA', 'NI', 'LU',
      ];
      this.used = new Set();
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onMouseEnter = this.onMouseEnter.bind(this);
      this.onMouseLeave = this.onMouseLeave.bind(this);
      this.onIntersect = this.onIntersect.bind(this);
      this.onReduceMotionChange = this.onReduceMotionChange.bind(this);
    }

    connectedCallback() {
      this.avatarsEl = this.querySelector('[data-social-proof-avatars]');
      if (!this.avatarsEl) return;

      this.count = Math.min(5, Math.max(3, Number(this.dataset.count) || 3));
      this.intervalMs = Math.max(3, Number(this.dataset.interval) || 4) * 1000;
      this.reduceMotionMq = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.canHoverMq = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.reducedMotion = this.reduceMotionMq.matches;
      this.canHover = this.canHoverMq.matches;
      this.avatarsEl.style.setProperty('--sp-count', String(this.count));

      this.renderInitial();

      document.addEventListener('visibilitychange', this.onVisibilityChange);

      // Desktop-only hover pause — iOS sticky :hover must not stop rotation forever
      if (this.canHover) {
        this.addEventListener('mouseenter', this.onMouseEnter);
        this.addEventListener('mouseleave', this.onMouseLeave);
      }

      if (this.reduceMotionMq.addEventListener) {
        this.reduceMotionMq.addEventListener('change', this.onReduceMotionChange);
      } else if (this.reduceMotionMq.addListener) {
        this.reduceMotionMq.addListener(this.onReduceMotionChange);
      }

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(this.onIntersect, {
          root: null,
          // Any visible pixel counts — Safari address bar made 0.25 too strict
          threshold: 0,
          rootMargin: '40px 0px 40px 0px',
        });
        this.observer.observe(this);
      }

      this.start();
    }

    disconnectedCallback() {
      this.stop();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.removeEventListener('mouseenter', this.onMouseEnter);
      this.removeEventListener('mouseleave', this.onMouseLeave);
      if (this.reduceMotionMq) {
        if (this.reduceMotionMq.removeEventListener) {
          this.reduceMotionMq.removeEventListener('change', this.onReduceMotionChange);
        } else if (this.reduceMotionMq.removeListener) {
          this.reduceMotionMq.removeListener(this.onReduceMotionChange);
        }
      }
      if (this.observer) this.observer.disconnect();
    }

    randomInitials() {
      let pick = this.pool[Math.floor(Math.random() * this.pool.length)];
      let guard = 0;
      while (this.used.has(pick) && guard < 40) {
        pick = this.pool[Math.floor(Math.random() * this.pool.length)];
        guard += 1;
      }
      this.used.add(pick);
      return pick;
    }

    createAvatar(slot, toneIndex, withEnter) {
      const el = document.createElement('span');
      el.className = 'social-proof__avatar social-proof__avatar--tone-' + (toneIndex % this.tones);
      el.textContent = this.randomInitials();
      el.style.setProperty('--sp-slot', String(slot));
      el.style.zIndex = String(10 + slot);
      if (withEnter) el.classList.add('is-enter');
      return el;
    }

    liveAvatars() {
      return Array.from(this.avatarsEl.querySelectorAll('.social-proof__avatar:not(.is-exit)'));
    }

    place(el, slot) {
      el.style.setProperty('--sp-slot', String(slot));
      el.style.zIndex = String(10 + Math.max(0, slot));
    }

    renderInitial() {
      this.avatarsEl.replaceChildren();
      this.used.clear();
      for (let i = 0; i < this.count; i += 1) {
        this.avatarsEl.appendChild(this.createAvatar(i, i, false));
      }
    }

    canRun() {
      return !this.reducedMotion && !this.isHovering && this.isInView && !document.hidden && !this.isAnimating;
    }

    start() {
      this.stop();
      if (!this.canRun()) return;
      this.timer = window.setInterval(() => this.tick(), this.intervalMs);
    }

    stop() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }

    tick() {
      if (!this.canRun() || !this.avatarsEl) return;

      const nodes = this.liveAvatars();
      if (nodes.length === 0) return;

      this.isAnimating = true;

      const outgoing = nodes[0];
      // Park outgoing one step further left while fading
      this.place(outgoing, -1);
      outgoing.classList.add('is-exit');

      // Slide everyone else one slot left
      nodes.slice(1).forEach((el, i) => {
        this.place(el, i);
      });

      // New avatar starts just outside on the right, then slides into last slot
      const incoming = this.createAvatar(this.count, this.count - 1, true);
      this.avatarsEl.appendChild(incoming);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.place(incoming, this.count - 1);
          incoming.classList.remove('is-enter');
        });
      });

      window.setTimeout(() => {
        if (outgoing.parentNode) {
          this.used.delete(outgoing.textContent);
          outgoing.remove();
        }
        // Normalize slots 0..count-1
        this.liveAvatars().forEach((el, i) => {
          this.place(el, i);
          for (let t = 0; t < this.tones; t += 1) {
            el.classList.remove('social-proof__avatar--tone-' + t);
          }
          el.classList.add('social-proof__avatar--tone-' + (i % this.tones));
        });
        this.isAnimating = false;
      }, this.transitionMs);
    }

    onVisibilityChange() {
      if (document.hidden) this.stop();
      else this.start();
    }

    onMouseEnter() {
      if (!this.canHover) return;
      this.isHovering = true;
      this.stop();
    }

    onMouseLeave() {
      if (!this.canHover) return;
      this.isHovering = false;
      this.start();
    }

    onReduceMotionChange(event) {
      this.reducedMotion = event.matches;
      if (this.reducedMotion) this.stop();
      else this.start();
    }

    onIntersect(entries) {
      const entry = entries[0];
      if (!entry) return;
      this.isInView = entry.isIntersecting;
      if (this.isInView) this.start();
      else this.stop();
    }
  }

  customElements.define('social-proof', SocialProof);
}
